import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

const DEFAULT_SUBJECT = "<CHILD>'s learning journey report";

const DEFAULT_BODY = [
  "Hello,",
  "",
  "Please find attached <CHILD>'s learning journey report from Bee Happy Preschool.",
  "",
  "If you have any questions about the report, do just let us know.",
  "",
  "Kind regards,",
  "Bee Happy Preschool",
].join("\n");

export interface EmailDraftTemplate {
  subject: string;
  body: string;
}

export const defaultEmailTemplate = (): EmailDraftTemplate => ({
  subject: DEFAULT_SUBJECT,
  body: DEFAULT_BODY,
});

interface EmailDraftModalProps {
  open: boolean;
  childName: string;
  template: EmailDraftTemplate;
  onTemplateChange: (template: EmailDraftTemplate) => void;
  creating: boolean;
  onCancel: () => void;
  onCreate: () => void;
}

/**
 * Editor for the email that wraps the report PDF draft. Subject and body may
 * contain <CHILD>, which is replaced with the child's first name when the
 * draft is created.
 */
const EmailDraftModal: React.FC<EmailDraftModalProps> = ({
  open,
  childName,
  template,
  onTemplateChange,
  creating,
  onCancel,
  onCreate,
}) => {
  const [touched, setTouched] = useState(false);

  const handleSubjectChange = (value: string) => {
    setTouched(true);
    onTemplateChange({ ...template, subject: value });
  };

  const handleBodyChange = (value: string) => {
    setTouched(true);
    onTemplateChange({ ...template, body: value });
  };

  const canCreate = !creating && Boolean(template.subject.trim() && template.body.trim());

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Email draft to parent</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Subject"
          value={template.subject}
          onChange={(event) => handleSubjectChange(event.target.value)}
          fullWidth
          margin="dense"
          size="small"
          helperText="<CHILD> will be replaced with the child's first name."
        />
        <TextField
          label="Email body"
          value={template.body}
          onChange={(event) => handleBodyChange(event.target.value)}
          fullWidth
          multiline
          minRows={9}
          margin="dense"
          helperText={
            touched && childName
              ? `<CHILD> will be replaced with ${childName}.`
              : "<CHILD> will be replaced with the child's first name."
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={creating}>
          Cancel
        </Button>
        <Button
          onClick={onCreate}
          variant="contained"
          color="primary"
          disabled={!canCreate}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {creating ? "Creating Draft..." : "Create Draft"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDraftModal;
